import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import { useEffect, useState } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";

import { ArticleAcknowledgementGateOverlay } from "~/components/article/article-acknowledgement-gate-overlay";
import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";
import { acknowledgementGateArticleIds } from "~/data";
import type { hooksArticleAcknowledgementGateOverlayArticle$key } from "~/queries/__generated__/hooksArticleAcknowledgementGateOverlayArticle.graphql";
import type { hooksContentInterestArticle$key } from "~/queries/__generated__/hooksContentInterestArticle.graphql";

import { UrlSectionNameMap } from "./consts";

type Sentiment = Record<string, unknown> & {
  readability_school_level: string[];
  sentiment_category: string;
};

export const useArticleAcknowledgementGateOverlay = (
  article_: hooksArticleAcknowledgementGateOverlayArticle$key,
) => {
  const { entityId: currentArticleId } = useFragment(
    graphql`
      fragment hooksArticleAcknowledgementGateOverlayArticle on Article {
        entityId
      }
    `,
    article_,
  );

  const [acknowledgementAccepted, setAcknowledgementAccepted] = useState(false);
  const isAcknowledgementRequired =
    acknowledgementGateArticleIds.has(currentArticleId) && !acknowledgementAccepted;

  const renderAcknowledgementGateOverlay = () => (
    <>
      {isAcknowledgementRequired && (
        <ArticleAcknowledgementGateOverlay
          handleAcceptAcknowledgement={() => setAcknowledgementAccepted(true)}
        />
      )}
    </>
  );

  return {
    isAcknowledgementRequired,
    renderAcknowledgementGateOverlay,
  };
};

export const useTrackContentInterest = (article_: hooksContentInterestArticle$key | undefined) => {
  useEffect(() => {
    if (!notEmpty(article_)) return;

    const article = readInlineData(
      graphql`
        fragment hooksContentInterestArticle on Article @inline {
          entityId
          sentiment
          urlAlias
          types {
            value {
              name
            }
          }
          topics {
            name
          }
        }
      `,
      article_,
    );

    const [mainType] = first(article?.types ?? [])?.value ?? [];
    const contentType = `Article - ${mainType?.name ?? "Standard"}`;

    const sentiment = (article?.sentiment ?? {}) as Sentiment;
    const readabilitySchoolLevel = sentiment?.readability_school_level ?? [];
    const sentimentCategory = sentiment?.sentiment_category ?? "";
    const contentTopics =
      article?.topics?.map?.(topic => topic?.name ?? "").filter?.(name => notEmpty(name)) ?? [];

    const matchedPath =
      Object.keys(UrlSectionNameMap).find(path => article?.urlAlias?.startsWith?.(path)) ?? "";
    const sectionName = UrlSectionNameMap[matchedPath] ?? "";

    sendOnePlusXEvent({
      articleID: article.entityId,
      contentTopics: contentTopics?.join("|"),
      contentType,
      readability_school_level: readabilitySchoolLevel.join("|"),
      sentiment_category: sentimentCategory,
      siteVerticals: sectionName,
      type: "scmp-content-interests",
    });
  }, [article_]);
};
